import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { IconArrowRight, IconPause, IconPlay } from '../components/ui/icons';
import { pseudoPeaks } from '../components/waveform/model';
import { CosignBar } from '../components/CosignBar';
import { ApiError, listings } from '../lib/api';
import { track } from '../lib/analytics';
import { apiErrorMessage } from '../lib/errors';
import { useAuth } from '../hooks/useAuth';
import { usePreviewPlayer } from '../hooks/usePreviewPlayer';
import { useToast } from '../components/ui/Toast';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { buildListingMeta, buildListingJsonLd } from '../lib/og';
import type { ListingDetail as ListingDetailType } from '../lib/types';

export function ListingDetail() {
  const { id = '' } = useParams();
  const { t, fmt } = useI18n();
  const { status } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [listing, setListing] = useState<ListingDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const preview = usePreviewPlayer(id);
  const previewPlaying = preview.state === 'playing';

  // Shareable card head (Wingspan cut 9). Server-rendered for crawlers by
  // functions/market/[id].ts; this mirrors it for in-app navigation.
  const meta = useMemo(() => {
    if (!listing) return null;
    const card = {
      id: listing.id,
      title: listing.title,
      description: listing.description,
      price_cents: listing.price_cents,
      price_currency: listing.price_currency,
      license_type: listing.license_type,
      creator: listing.creator
        ? { id: listing.creator.id, display_name: listing.creator.display_name, handle: listing.creator.handle }
        : null,
      asset: listing.asset,
    };
    return {
      title: `${listing.title} — DJMEXXICO`,
      tags: buildListingMeta(card),
      jsonLd: buildListingJsonLd(card),
    };
  }, [listing]);
  useDocumentMeta(meta);

  useEffect(() => {
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      toast.push(t.market.checkoutSuccess, 'success');
      // Funnel close: Stripe only redirects here after a settled payment, so this
      // is a real completed sale — the one honest client signal for it.
      track('sale_completed', { listing_id: id });
    } else if (checkout === 'cancel') toast.push(t.market.checkoutCancel, 'info');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listings
      .get(id)
      .then((l) => {
        if (!active) return;
        setListing(l);
        track('listing_viewed', {
          listing_id: l.id,
          price_cents: l.price_cents,
          price_currency: l.price_currency,
          license_type: l.license_type,
        });
      })
      .catch((err) => active && setError(apiErrorMessage(err, t)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id, t]);

  async function buy() {
    if (status !== 'authenticated') {
      navigate('/auth', { state: { from: `/market/${id}` } });
      return;
    }
    setCheckingOut(true);
    track('checkout_started', {
      kind: 'listing',
      listing_id: id,
      price_cents: listing?.price_cents,
      price_currency: listing?.price_currency,
      license_type: listing?.license_type,
    });
    try {
      const res = await listings.checkout(id);
      window.location.href = res.checkout_url;
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) {
        navigate('/auth', { state: { from: `/market/${id}` } });
        return;
      }
      toast.push(apiErrorMessage(err, t), 'error');
      setCheckingOut(false);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <Link to="/market" className="type-label text-secondary hover:text-primary focus-ring inline-flex rounded-sm">
          ← {t.market.title}
        </Link>
      </div>

      {loading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : error ? (
        <p className="type-body-sm text-danger" role="alert">
          {error}
        </p>
      ) : listing ? (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div>
            <PageHeader title={listing.title} />
            {listing.creator ? (
              <div className="-mt-2 mb-4">
                <Link
                  to={`/u/${listing.creator.id}`}
                  className="type-label text-secondary hover:text-accent focus-ring inline-flex items-center gap-1 rounded-sm"
                >
                  {t.market.byCreator} · {listing.creator.display_name}
                </Link>
              </div>
            ) : null}
            <Card className="p-4">
              <div className="flex h-40 items-center gap-px" aria-hidden>
                {pseudoPeaks(listing.asset_id, 96).map((p, i) => (
                  <span
                    key={i}
                    className={`w-full ${previewPlaying ? 'wave-playing bg-wave-hot' : 'bg-wave-rest'}`}
                    style={{
                      height: `${Math.max(6, p * 100)}%`,
                      ...(previewPlaying ? { animationDelay: `${(i % 16) * 55}ms` } : undefined),
                    }}
                  />
                ))}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={preview.toggle}
                  disabled={preview.state === 'unavailable'}
                  loading={preview.state === 'loading'}
                  aria-pressed={previewPlaying}
                  iconLeft={previewPlaying ? <IconPause size={16} /> : <IconPlay size={16} />}
                >
                  {previewPlaying ? t.market.pause : t.market.preview}
                </Button>
                {preview.state === 'unavailable' ? (
                  <span className="type-caption text-muted">{t.market.previewUnavailable}</span>
                ) : null}
              </div>
            </Card>
            {listing.description ? (
              <p className="type-body text-secondary mt-6 whitespace-pre-line">{listing.description}</p>
            ) : null}
          </div>

          {/* Commerce rail */}
          <aside className="lg:sticky lg:top-4 lg:self-start">
            <Card className="p-5">
              <div className="text-right">
                <span className="type-price text-accent block tnum" style={{ fontSize: '32px', lineHeight: '36px' }}>
                  {fmt.money(listing.price_cents, listing.price_currency)}
                </span>
              </div>
              <dl className="mt-5 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <dt className="type-label text-muted">{t.listing.licenseLabel}</dt>
                  <dd className="type-data text-primary">{t.listing.license[listing.license_type]}</dd>
                </div>
              </dl>
              <div className="mt-5">
                <Button size="lg" block loading={checkingOut} onClick={buy}>
                  {checkingOut ? t.market.checkoutOpening : t.market.buy}
                </Button>
              </div>
              <p className="type-caption text-muted mt-3">{t.market.licenseLine(t.listing.license[listing.license_type])}</p>
              {/* F-055: the co-sign social proof + action, staked right where the
                  pack is judged. */}
              <CosignBar
                listingId={listing.id}
                creatorId={listing.creator_id}
                initialCount={listing.cosign_count}
                initialViewerHasCosigned={listing.viewer_has_cosigned}
              />
            </Card>
            <div className="mt-4 text-center">
              <Link to="/market" className="type-label text-secondary hover:text-primary focus-ring inline-flex items-center gap-1 rounded-sm">
                {t.market.title}
                <IconArrowRight size={14} />
              </Link>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
